import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import Config from "../api/Config";

/* ─── Constants ─── */
const BASE_URL =
  process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "https://berrybloomer.com/api";
const DEFAULT_IMAGE = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%231e293b'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2364748b' font-size='14' font-family='sans-serif'%3ENo Logo%3C/text%3E%3C/svg%3E`;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"];

/**
 * FIX 1 — field-name defensiveness.
 * If the Spring Boot list endpoint returns the filename under a different
 * key than "imageName" (fileName / logoUrl / image / filename / logo),
 * this still finds it instead of silently falling back to the placeholder.
 * Remove this fallback list once you confirm the actual field name in the
 * network response and lock it to just that one key.
 */
const encodeSegments = (path) =>
  path
    .replace(/^\/+/, "")
    .split("/")
    .map(encodeURIComponent)
    .join("/");

/**
 * Returns an ordered list of URLs to try for a given client record.
 * Both previous guesses (full nested path, bare filename) 404'd, which
 * means the whole "/client-logo/image/..." route may not be backed by
 * anything at all on the server — so this also tries a few structurally
 * different shapes: by-id, static-resource-without-/image, and a
 * root-relative path in case files are served as plain static assets
 * outside the /api prefix. This is still guessing without the actual
 * backend mapping — testing a candidate URL directly in a browser tab
 * is the fastest way to confirm which (if any) is correct.
 */
const buildImageCandidates = (client) => {
  const filePath = client?.filePath; // e.g. "client_logos/6.png"
  const fileName =
    client?.fileName ?? client?.imageName ?? client?.filename ?? null; // e.g. "6.png"
  const legacy = client?.logoUrl ?? client?.image ?? client?.logo ?? null;
  const id = client?.id;

  // BASE_URL is typically ".../api" — derive the origin (no /api) for
  // candidates that assume static files are served from server root.
  let origin = null;
  try {
    origin = new URL(BASE_URL).origin;
  } catch {
    origin = null;
  }

  const candidates = [];
  if (filePath) candidates.push(`${BASE_URL}/client-logo/image/${encodeSegments(filePath)}`);
  if (fileName && fileName !== filePath) {
    candidates.push(`${BASE_URL}/client-logo/image/${encodeSegments(fileName)}`);
  }
  if (id != null) candidates.push(`${BASE_URL}/client-logo/${id}/image`);
  if (filePath) candidates.push(`${BASE_URL}/${encodeSegments(filePath)}`);
  if (origin && filePath) candidates.push(`${origin}/${encodeSegments(filePath)}`);
  // Confirmed server layout: file.upload-dir=/var/www/BB_images with a
  // client_logos/ subfolder — try a common "/images/**" static-resource
  // prefix in case that's how it's exposed over HTTP.
  if (filePath) candidates.push(`${BASE_URL}/images/${encodeSegments(filePath)}`);
  if (origin && filePath) candidates.push(`${origin}/images/${encodeSegments(filePath)}`);
  if (legacy) {
    candidates.push(
      legacy.startsWith("http://") || legacy.startsWith("https://")
        ? legacy
        : `${BASE_URL}/client-logo/image/${encodeSegments(legacy)}`,
    );
  }
  return candidates.length ? candidates : [DEFAULT_IMAGE];
};

/* ─── Animations ─── */
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

const fadeIn = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

/* ─── Styled Components ─── */
const Page = styled.div`
  min-height: 100vh;
  padding: 48px 28px 80px;
  font-family: "Poppins", sans-serif;
  color: #e2e8f0;
  max-width: 1200px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 36px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  background: linear-gradient(135deg, #fff 30%, #00c4ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const AddBtn = styled.button`
  background: linear-gradient(135deg, #5f2eea, #0a84ff);
  color: #fff;
  border: none;
  padding: 10px 22px;
  border-radius: 24px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.88;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
`;

const Card = styled(motion.div)`
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(160deg, #0f172a, #1e293b);
  border: 1px solid rgba(148, 163, 184, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const CardImg = styled.div`
  width: 100%;
  aspect-ratio: 4/3;
  background: #fff;
  overflow: hidden;
  position: relative;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: opacity 0.3s;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
`;

const ImgShimmer = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #1e293b 25%, #243044 50%, #1e293b 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite linear;
`;

const BrokenBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  background: rgba(220, 38, 38, 0.9);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  z-index: 2;
  word-break: break-all;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  padding: 14px 18px 18px;
`;

const ActionBtn = styled.button`
  flex: 1;
  padding: 8px;
  border-radius: 10px;
  border: none;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  background: ${({ $variant }) =>
    $variant === "delete"
      ? "rgba(248, 113, 113, 0.12)"
      : "rgba(0, 196, 255, 0.1)"};
  color: ${({ $variant }) => ($variant === "delete" ? "#f87171" : "#00c4ff")};
  border: 1px solid
    ${({ $variant }) =>
      $variant === "delete" ? "rgba(248,113,113,0.25)" : "rgba(0,196,255,0.2)"};
  &:hover {
    opacity: 0.75;
  }
`;

const StatusText = styled.p`
  color: ${({ $error }) => ($error ? "#f87171" : "#475569")};
  font-size: 0.9rem;
  margin: 40px 0;
  text-align: center;
`;

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Modal = styled(motion.div)`
  background: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 20px;
  padding: 32px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
`;

const ModalTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 24px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const FileInput = styled.input`
  font-size: 0.85rem;
  color: #94a3b8;
  background: #1e293b;
  border: 1px dashed rgba(148, 163, 184, 0.25);
  border-radius: 10px;
  padding: 10px 14px;
  cursor: pointer;
  width: 100%;
  &::file-selector-button {
    background: rgba(0, 196, 255, 0.1);
    color: #00c4ff;
    border: 1px solid rgba(0, 196, 255, 0.2);
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 0.8rem;
    cursor: pointer;
    margin-right: 10px;
  }
`;

const FileHint = styled.span`
  font-size: 0.72rem;
  color: #475569;
`;

const PreviewImg = styled.img`
  margin-top: 10px;
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: contain;
  background: #fff;
  padding: 12px;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.15);
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
`;

const ModalBtn = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 12px;
  border: none;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  background: ${({ $primary }) =>
    $primary
      ? "linear-gradient(135deg,#5f2eea,#0a84ff)"
      : "rgba(148,163,184,0.1)"};
  color: ${({ $primary }) => ($primary ? "#fff" : "#94a3b8")};
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ErrMsg = styled.p`
  color: #f87171;
  font-size: 0.8rem;
  margin-top: 8px;
`;

const ConfirmText = styled.p`
  color: #94a3b8;
  font-size: 0.9rem;
  margin: 0 0 24px;
  line-height: 1.6;
`;

const SkeletonCard = styled.div`
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(160deg, #0f172a, #1e293b);
  border: 1px solid rgba(148, 163, 184, 0.06);
  .sk-img {
    aspect-ratio: 4/3;
    background: linear-gradient(90deg, #1e293b 25%, #243044 50%, #1e293b 75%);
    background-size: 800px 100%;
    animation: ${shimmer} 1.6s infinite linear;
  }
  .sk-body {
    padding: 16px 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .sk-line {
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(90deg, #1e293b 25%, #243044 50%, #1e293b 75%);
    background-size: 800px 100%;
    animation: ${shimmer} 1.6s infinite linear;
  }
`;

/* ─── ClientCard ─── */
function ClientCard({ client, onDelete }) {
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  const candidates = useRef(buildImageCandidates(client)).current;
  const [attemptIndex, setAttemptIndex] = useState(0);
  const src = candidates[attemptIndex] ?? DEFAULT_IMAGE;

  return (
    <Card variants={fadeIn} initial="hidden" animate="visible" exit="exit" layout>
      <CardImg>
        {!loaded && <ImgShimmer />}
        {/* Surfaces the failure instead of hiding it behind a silent
            placeholder swap, once every candidate URL has been tried. */}
        {broken && (
          <BrokenBadge title={candidates.join(" | ")}>
            404 (tried {candidates.length} path{candidates.length > 1 ? "s" : ""}):{" "}
            {candidates[candidates.length - 1] || "no image field on record"}
          </BrokenBadge>
        )}
        <Img
          src={src}
          alt="Client logo"
          $loaded={loaded}
          onLoad={() => setLoaded(true)}
          onError={() => {
            const nextIndex = attemptIndex + 1;
            if (nextIndex < candidates.length) {
              // Try the next candidate shape (e.g. bare filename after the
              // full nested path failed, or vice versa) before giving up.
              console.warn(
                `[ClientCard] "${src}" failed, trying next candidate:`,
                candidates[nextIndex],
              );
              setAttemptIndex(nextIndex);
              return;
            }
            console.warn("[ClientCard] all candidate URLs failed:", {
              candidates,
              client,
            });
            setBroken(true);
            setLoaded(true);
          }}
        />
      </CardImg>

      <CardActions>
        <ActionBtn $variant="delete" onClick={() => onDelete(client)}>
          Delete
        </ActionBtn>
      </CardActions>
    </Card>
  );
}

/* ─── Main Page ─── */
export default function Client() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // 'add' | 'delete'
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  /* ─── Fetch ─── */
  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await Config.get("/client-logo");
      if (!Array.isArray(data)) throw new Error("Bad response shape");
      // FIX 4 — log the raw shape once so you can confirm the actual
      // image field name in the console (remove after confirming).
      if (data[0]) console.log("[Client] sample client record:", data[0]);
      setClients(data);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Could not load clients.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  /* ─── File change (jpg/png only, with validation) ─── */
  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    setFormErr("");

    if (!file) {
      setLogoFile(null);
      setPreview(null);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setFormErr("Only JPG or PNG images are allowed for the logo.");
      e.target.value = "";
      setLogoFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFormErr("Logo image must be smaller than 5MB.");
      e.target.value = "";
      setLogoFile(null);
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  /* ─── Modals ─── */
  const openAdd = () => {
    setLogoFile(null);
    setPreview(null);
    setFormErr("");
    setSelected(null);
    setModal("add");
  };

  const openDelete = (client) => {
    setSelected(client);
    setFormErr("");
    setModal("delete");
  };

  const closeModal = () => {
    setModal(null);
    setSelected(null);
    setSaving(false);
    setFormErr("");
    setPreview(null);
    setLogoFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ─── Add ─── */
  // Backend expects @RequestParam MultipartFile file — field name MUST be "file"
  // (confirmed via backend log: "Required part 'file' is not present").
  //
  // IMPORTANT: do NOT manually set the Content-Type header for a FormData
  // request. Axios/the browser generates it automatically as
  // "multipart/form-data; boundary=----WebKitFormBoundary..." — the boundary
  // is what lets the server split the body into parts. Setting the header
  // by hand strips that boundary out, so the server sees no parts at all
  // even if the field name is correct.
  const handleAdd = async () => {
    if (!logoFile) return setFormErr("Please select a logo image.");
    setSaving(true);
    setFormErr("");
    try {
      const fd = new FormData();
      fd.append("file", logoFile);
      const res = await Config.post("/client-logo", fd);
      // FIX 5 — log what the backend returns right after upload. If the
      // saved filename it echoes back here differs from what later shows
      // up in the GET /client-logo list response, that mismatch IS the bug.
      console.log("[Client] upload response:", res?.data);
      await fetchClients();
      closeModal();
    } catch (err) {
      console.error("Add error:", err?.response?.data || err.message);
      setFormErr(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to add logo. Please check with the backend team.",
      );
    } finally {
      setSaving(false);
    }
  };

  /* ─── Delete ─── */
  const handleDelete = async () => {
    setSaving(true);
    setFormErr("");
    try {
      await Config.delete(`/client-logo/${selected.id}`);
      await fetchClients();
      closeModal();
    } catch (err) {
      console.error("Delete error:", err?.response?.data || err.message);
      setFormErr("Failed to delete client.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <TopBar>
        <PageTitle>Manage Clients</PageTitle>
        <AddBtn onClick={openAdd}>+ Add Logo</AddBtn>
      </TopBar>

      {error && <StatusText $error>{error}</StatusText>}

      {loading ? (
        <Grid>
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonCard key={i}>
              <div className="sk-img" />
              <div className="sk-body">
                <div className="sk-line" style={{ width: "55%" }} />
                <div className="sk-line" style={{ width: "85%" }} />
                <div className="sk-line" style={{ width: "70%" }} />
              </div>
            </SkeletonCard>
          ))}
        </Grid>
      ) : !error && clients.length === 0 ? (
        <StatusText>No logos yet. Click "+ Add Logo" to get started.</StatusText>
      ) : (
        <Grid>
          <AnimatePresence>
            {clients.map((c) => (
              <ClientCard
                key={c.id}
                client={c}
                onDelete={openDelete}
              />
            ))}
          </AnimatePresence>
        </Grid>
      )}

      {/* ── Add Modal ── */}
      <AnimatePresence>
        {modal === "add" && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && closeModal()}
          >
            <Modal
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <ModalTitle>Add Client Logo</ModalTitle>

              <Field>
                <Label>Upload Logo</Label>

                <FileInput
                  type="file"
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  ref={fileRef}
                  onChange={handleFileChange}
                />

                <FileHint>JPG or PNG only. Maximum size: 5MB.</FileHint>

                {preview && <PreviewImg src={preview} alt="Logo preview" />}
              </Field>

              {formErr && <ErrMsg>{formErr}</ErrMsg>}

              <ModalActions>
                <ModalBtn onClick={closeModal} disabled={saving}>
                  Cancel
                </ModalBtn>

                <ModalBtn
                  $primary
                  onClick={handleAdd}
                  disabled={saving}
                >
                  {saving ? "Uploading..." : "Upload Logo"}
                </ModalBtn>
              </ModalActions>
            </Modal>
          </Overlay>
        )}
      </AnimatePresence>

      {/* ── Delete Confirm Modal ── */}
      <AnimatePresence>
        {modal === "delete" && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && closeModal()}
          >
            <Modal
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <ModalTitle>Delete Logo</ModalTitle>
              <ConfirmText>
                Are you sure you want to delete this client logo? This action
                cannot be undone.
              </ConfirmText>

              {formErr && <ErrMsg>{formErr}</ErrMsg>}

              <ModalActions>
                <ModalBtn onClick={closeModal} disabled={saving}>
                  Cancel
                </ModalBtn>
                <ModalBtn
                  $primary
                  onClick={handleDelete}
                  disabled={saving}
                  style={{
                    background: "linear-gradient(135deg,#dc2626,#f87171)",
                  }}
                >
                  {saving ? "Deleting..." : "Yes, Delete"}
                </ModalBtn>
              </ModalActions>
            </Modal>
          </Overlay>
        )}
      </AnimatePresence>
    </Page>
  );
}